import { useRef, useState } from "react";
import { Banner, Button, Field, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeVersion } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateThemeVersionMetaMutation } from "../../services/useUpdateThemeVersionMetaMutation";
import { useCreateThemeThumbnailUploadMutation } from "../../services/useCreateThemeThumbnailUploadMutation";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * The version's gallery thumbnail — the image sellers see on the theme card.
 *
 * Upload flow (mirrors the store-logo / payment-proof uploads): pick a file →
 * request a presigned PUT (`/admin/uploads/theme-thumbnails`) → PUT the file to
 * R2 → persist the returned `publicUrl` into the version's `thumbnailUrl`
 * metadata.
 *
 * Deliberately separate from the theme JSON and from Save: `thumbnailUrl` is
 * version METADATA, and the backend allows writing it even on a published
 * (frozen) version — immutability is about the rendered payload, not the
 * marketing image. That is also why ThemeStudio renders this field regardless
 * of read-only: a published theme's thumbnail must stay editable.
 *
 * Removing is reversible through the same metadata write, so it happens at
 * once with an Undo that puts the previous image back. A failed upload or
 * save is a Banner here with Try again; a wrong file type is a field error.
 */
export function ThumbnailField({
  themeId,
  version,
}: {
  themeId: string;
  version: ThemeVersion;
}) {
  const t = useTranslations();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(version.thumbnailUrl ?? "");
  /** A wrong file: said under the field, with how to fix it. */
  const [fileError, setFileError] = useState<string | null>(null);
  /** A failed request: what failed, and how to repeat it. */
  const [failure, setFailure] = useState<{ title: string; detail: string; retry: () => void } | null>(null);
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const { mutateAsync: createUpload } =
    useCreateThemeThumbnailUploadMutation();
  const { mutateAsync: saveMeta } = useUpdateThemeVersionMetaMutation();

  const hasImage = value.trim().length > 0;

  const detailOf = (err: unknown) => (err instanceof ApiJsonClientError ? err.message || "" : "");

  const handleFile = async (file: File) => {
    setFileError(null);
    setFailure(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError(t("themes.thumbnailTypeError"));
      return;
    }
    const uploadFailed = (detail: string) =>
      setFailure({ title: t("themes.thumbnailUploadFailedTitle"), detail, retry: () => void handleFile(file) });
    setBusy("upload");
    try {
      const intent = await createUpload({
        themeId,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      try {
        const putResponse = await fetch(intent.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!putResponse.ok) {
          uploadFailed(t(keys.products.uploadFailed));
          return;
        }
      } catch (uploadError) {
        uploadFailed(
          uploadError instanceof TypeError
            ? t(keys.errors.uploadBlockedByBrowser)
            : t(keys.products.uploadFailed),
        );
        return;
      }
      const updated = await saveMeta({
        themeId,
        versionId: version.id,
        thumbnailUrl: intent.publicUrl,
      });
      setValue(updated.thumbnailUrl ?? intent.publicUrl);
      toast.show({ message: t("themes.thumbnailSavedToast", { version: version.version }) });
    } catch (err) {
      uploadFailed(detailOf(err));
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  /** Writes `next` as the thumbnail; `previous` (when given) is offered back as Undo. */
  const writeThumbnail = async (next: string, previous: string | null) => {
    setFileError(null);
    setFailure(null);
    setBusy("remove");
    try {
      const updated = await saveMeta({
        themeId,
        versionId: version.id,
        thumbnailUrl: next,
      });
      setValue(updated.thumbnailUrl ?? next);
      if (previous !== null) {
        toast.show({
          tone: "neutral",
          message: t("themes.thumbnailRemovedToast", { version: version.version }),
          actionLabel: t("themes.undo"),
          onAction: () => void writeThumbnail(previous, null),
        });
      } else {
        toast.show({ tone: "neutral", message: t("themes.thumbnailRestoredToast", { version: version.version }) });
      }
    } catch (err) {
      setFailure({
        title: t(next ? "themes.thumbnailRestoreFailedTitle" : "themes.thumbnailRemoveFailedTitle"),
        detail: detailOf(err),
        retry: () => void writeThumbnail(next, previous),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rd-card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      {failure ? (
        <Banner
          tone="error"
          title={failure.title}
          actions={
            <Button size="sm" loading={busy !== null} loadingLabel={t(keys.shell.retrying)} onClick={failure.retry}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {[failure.detail, t("themes.nothingChanged")].filter(Boolean).join(" ")}
        </Banner>
      ) : null}
      <Field label={t(keys.themes.thumbnail)} error={fileError}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          {/* eslint-disable-next-line no-restricted-syntax -- a hidden file picker opened by the Upload button; the kit has no file control. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy === "remove"}
            loading={busy === "upload"}
            loadingLabel={t(keys.products.uploadingImage)}
            onClick={() => inputRef.current?.click()}
          >
            {hasImage ? t(keys.seller.storeLogoReplace) : t(keys.common.upload)}
          </Button>
          {hasImage ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy === "upload"}
              loading={busy === "remove"}
              loadingLabel={t("themes.thumbnailRemoving")}
              onClick={() => void writeThumbnail("", value)}
            >
              {t(keys.common.remove)}
            </Button>
          ) : null}
        </div>
      </Field>
      {hasImage ? (
        <img
          src={value}
          alt={t(keys.themes.thumbnailPreviewAlt)}
          style={{
            maxHeight: 128,
            width: "auto",
            alignSelf: "flex-start",
            borderRadius: 8,
            boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
            objectFit: "contain",
          }}
        />
      ) : null}
    </div>
  );
}
